/**
 * Warded sites — the encounter at concept scale.
 *
 * The MVP left a hole at the scale a student actually learns at. A boss is a
 * kingdom ceremony and a quest owns no mathematics at all, so the ordinary
 * study cycle — read the idea, work the problems, say what you learned — had no
 * home in the adventure: the student left it, read a lesson, ground a practice
 * set, and walked back in.
 *
 * A *site* is one concept, wearing that cycle. It is entered from the concept
 * page or from a region map, it asks a handful of questions from the concept's
 * own templates, and it is worth exactly one small reward the first time.
 *
 * There is nothing authored here and there is nothing to author. A site is a
 * pure function of the concept and what the platform already believes about the
 * student, which is what lets all 266 teachable concepts have one on the day
 * this ships rather than the eleven somebody found time to write.
 *
 * Three rules, and the first two are the ones most easily lost at this scale.
 *
 * **A site withholds nothing.** It is a *presentation* of practice, exactly as
 * a kingdom is a presentation of the graph. The concept page, the lesson and
 * `/practice/:conceptId` all stay one tap away and stay reachable to a student
 * who has never opened the adventure. Nothing in this file answers "may this
 * student open this lesson" and nothing added to it ever should.
 *
 * **Losing costs nothing, and a site is gentler than a dragon.** `SITE_PASS_SHARE`
 * is three of five rather than a boss's three of four: a ceremony you walk up
 * to deliberately may ask for near-perfection, and the thing standing on the
 * road you were walking down anyway may not.
 *
 * **The reward is paid once and is small.** `docs/rpg.md` §47 — a student who
 * fights the same site fifty times must not out-earn one who moved on. After
 * the first clear a site pays what practice pays and nothing more, through the
 * same idempotent claim a quest and a boss go through.
 */

import type { Difficulty } from '@yuan-math/exercises';
import { claimQuestReward, type LearnerProfile } from '@yuan-math/learning';
import type { EncounterDef } from './encounter.ts';
import type { QuestReward } from './quests.ts';

/**
 * How many questions a site asks.
 *
 * Five, and the reasons are the same ones bounding a boss stage: below three
 * the pass share stops meaning anything, and a site long enough to be a
 * practice set would be a practice set with a costume on. A student who wants
 * eight more has `/practice/:conceptId`, unchanged and one tap away.
 */
export const SITE_QUESTIONS = 5;

/** Three of five. Gentler than a boss on purpose — see the note above. */
export const SITE_PASS_SHARE = 0.6;

/**
 * What clearing one pays, once, ever.
 *
 * Deliberately below a quest, which is below a boss. A site is a few minutes'
 * work on one concept; a reward that made it competitive with a quest line
 * would make grinding the easiest concept in the graph the best move in the
 * game, which is the exact failure §47 names.
 */
export const SITE_XP = 40;
export const SITE_GOLD = 8;
export const SITE_REWARD: QuestReward = { xp: SITE_XP, gold: SITE_GOLD };

/**
 * The key a site's fight is stored under, and the id its reward is claimed
 * under.
 *
 * One prefix serving both, in two different maps: `battles` (keyed by battle
 * id) and `claimedQuestIds` (shared with quests and `boss:` claims). The same
 * namespacing trick `bossClaimId` uses, and it is what keeps a site from ever
 * colliding with a quest id or a boss id. `validateQuests` refuses a quest id
 * starting with either prefix.
 */
export const SITE_PREFIX = 'site:';
export const siteBattleId = (conceptId: string): string => `${SITE_PREFIX}${conceptId}`;
export const siteClaimId = (conceptId: string): string => `${SITE_PREFIX}${conceptId}`;

/**
 * Builds the fight at a site.
 *
 * One adaptive stage over one concept: it opens at `band` and then moves with
 * the student (`adaptiveBand`), which is §16's whole ask and the reason
 * `encounter.ts` grew a `Banding` at all. A boss's stages stay fixed; a thing
 * you meet on the road adapts.
 *
 * **`band` is passed in, not computed here, and that is load-bearing.** The
 * obvious signature takes the student's mastery and calls `openingBand` — and
 * it is wrong, because mastery *moves as the student answers*. A definition
 * rebuilt from live mastery would change its opening band mid-fight, and the
 * band is part of the seed, so the questions would be re-drawn underneath
 * somebody halfway through them. Taking a band makes the caller responsible for
 * holding it still for the duration (`EncounterPage` keeps it in the URL, the
 * way the practice page keeps its seed there), and makes this function a pure
 * shape with nothing to drift.
 *
 * `openingBand` is exported from `encounter.ts` for the caller to use once.
 *
 * The stage's name is the concept's title rather than a fantasy one. The
 * *ghost* carries the fiction (`ghosts.ts`); a stage banner reading "The Prime
 * Flame" over a single-concept fight would be a difficulty band wearing a name,
 * which is the thing `BossStage` is documented not to be.
 */
export function siteEncounter(
  conceptId: string,
  conceptTitle: string,
  band: Difficulty,
  questionCount: number = SITE_QUESTIONS,
): EncounterDef {
  return {
    id: siteBattleId(conceptId),
    passShare: SITE_PASS_SHARE,
    stages: [
      {
        name: conceptTitle,
        conceptIds: [conceptId],
        difficulty: band,
        questionCount,
        banding: 'adaptive',
      },
    ],
  };
}

/** Whether this site's one-off reward has already been paid. */
export function siteCleared(conceptId: string, profile: LearnerProfile): boolean {
  return profile.rpg?.claimedQuestIds.includes(siteClaimId(conceptId)) ?? false;
}

/**
 * Pays for a cleared site, once.
 *
 * The same idempotent claim a quest and a boss settle through, so a site fought
 * again for the pleasure of it is paid nothing — and, unlike a boss, there is
 * nothing else to record. A site has no "cleared" flag anywhere: whether the
 * student can do the concept is a question the attempt log already answers
 * better than a boolean would, and `worldState` reads mastery for it.
 *
 * Anything other than a win returns the profile untouched.
 */
export function settleSiteVictory(
  profile: LearnerProfile,
  conceptId: string,
  outcome: string,
): LearnerProfile {
  if (outcome !== 'won') return profile;
  return claimQuestReward(profile, {
    questId: siteClaimId(conceptId),
    xp: SITE_REWARD.xp,
    gold: SITE_REWARD.gold,
  });
}
